<script lang="ts" setup>
import vipDiscountIcon from '@h5/assets/vip_discount.png'
import vipIcon from '@h5/assets/vip_icon2.png'
import { getChapterPayColors } from '@/composables/payment/configs/chapterColors'
import { getDailyPrice, getSavePercent, shouldShowDailyPrice } from '@/composables/payment/utils/subscribeDailyPrice'
import { primaryColor } from '@/config/brands'

const props = defineProps<{
  products: any[]
  isZero: (val: any) => boolean
  getUnitPrice: (item: any) => string
  isDiscount: (item: any) => boolean
  getIsImageBackground: (item: any) => boolean
}>()

const emit = defineEmits<{
  (e: 'select', item: any): void
}>()

const chapterColors = getChapterPayColors()

function getSubscribeItemStyle(item: any) {
  return {
    background: props.getIsImageBackground(item)
      ? `url(${chapterColors.memberSubscribeBg}) no-repeat`
      : chapterColors.subscribeActiveBg,
    backgroundSize: '100% 100%',
  }
}
</script>

<template>
  <div class="subscribe-list">
    <div
      v-for="(item, index) in products"
      :key="item.productId || index"
      class="subscribe-item"
      :style="getSubscribeItemStyle(item)"
      @click="emit('select', item)"
    >
      <!-- 折扣会员 -->
      <template v-if="isDiscount(item)">
        <!-- 周期水印 -->
        <div class="sub-cycle-watermark">
          {{ item.cycleStr }}
        </div>
        <img :src="vipDiscountIcon" class="sub-icon-img">
        <div class="sub-info">
          <div class="sub-coins">
            {{ item.discountCoins }} coins
          </div>
          <ul class="sub-benefits">
            <li>Get {{ item.discountOnceCoins }} Coins at once</li>
            <li>Earn {{ item.discountBonusCoins }} Coins ({{ item.discountDailyCoins }}/day) from daily login</li>
          </ul>
        </div>
        <div class="sub-pricing">
          <div class="sub-price">
            $ {{ item.preferentialPrice || item.price }}
          </div>
          <div v-if="item.discountOriginalPrice" class="sub-origin-price">
            $ {{ item.discountOriginalPrice }}
          </div>
        </div>
      </template>

      <!-- 畅读会员 -->
      <template v-else>
        <!-- cornerTip 标签 -->
        <span v-if="item.cornerTip && item.marketingCopy" class="subscribe-tag">
          {{ item.marketingCopy }}
        </span>
        <img :src="vipIcon" class="sub-icon-img">
        <div class="sub-info">
          <div class="sub-cycle-row">
            <div class="sub-cycle-label">
              {{ item.cycleStr }}
            </div>
            <div v-if="shouldShowDailyPrice(item, isZero)" class="sub-save-tag">
              SAVE {{ getSavePercent(item) }}%
            </div>
          </div>
          <div class="sub-desc">
            {{ item.subscribeText }}
          </div>
        </div>
        <div class="sub-pricing">
          <!-- 免费 -->
          <template v-if="isZero(item.preferentialPrice)">
            <div class="sub-price sub-free">
              Free
            </div>
            <div class="sub-origin-price">
              $ {{ item.price }}
            </div>
          </template>
          <!-- 有优惠价 -->
          <template v-else-if="item.preferentialPrice">
            <div class="sub-price">
              <template v-if="shouldShowDailyPrice(item, isZero)">
                $ {{ getDailyPrice(item) }}<span class="sub-price-unit"> / Day</span>
              </template>
              <template v-else>
                $ {{ item.preferentialPrice }}
              </template>
            </div>
            <div class="sub-origin-price">
              $ {{ item.price }}
            </div>
          </template>
          <!-- 原价 -->
          <template v-else>
            <div class="sub-price">
              $ {{ item.price }}
            </div>
            <div v-if="getUnitPrice(item)" class="sub-unit-price">
              $ {{ getUnitPrice(item) }}
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.subscribe-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 10px;
}

.subscribe-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 0 14px;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 415px;
  height: 92px;
  box-sizing: border-box;
}
.subscribe-item:hover {
  opacity: 0.9;
}

.sub-cycle-watermark {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 20px;
  font-weight: 700;
  color: v-bind(primaryColor);
  opacity: 0.25;
  line-height: 1;
  pointer-events: none;
}

.sub-icon-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;
}

.sub-info {
  flex: 1;
  min-width: 0;
}

.sub-coins {
  font-size: 16px;
  font-weight: 600;
  color: #32353A;
  margin-bottom: 2px;
}

.sub-benefits {
  list-style: disc;
  padding-left: 14px;
  margin: 0;
  font-size: 11px;
  color: v-bind(primaryColor);
  line-height: 1.6;
}

.sub-cycle-label {
  font-size: 15px;
  font-weight: 600;
  color: #32353A;
  margin-bottom: 2px;
}

.sub-desc {
  font-size: 12px;
  color: #9296A0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub-pricing {
  flex-shrink: 0;
  text-align: right;
}

.sub-price {
  font-size: 16px;
  font-weight: 700;
  color: #32353A;
}

.sub-free {
  color: v-bind(primaryColor);
}

.sub-origin-price {
  font-size: 12px;
  color: #C8C8C8;
  text-decoration: line-through;
  text-align: right;
}

.sub-unit-price {
  font-size: 12px;
  color: #9296A0;
  text-align: right;
}

.subscribe-tag {
  position: absolute;
  top: -8px;
  left: -1px;
  background: linear-gradient(132deg, #F961BC 0%, #F74984 100%);
  color: #fff;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 100px 100px 100px 0;
  white-space: nowrap;
  z-index: 1;
}

.sub-cycle-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sub-save-tag {
  background: rgba(247, 73, 132, 0.15);
  color: rgba(247, 73, 132, 1);
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  padding: 3px 6px 4px 6px;
  line-height: 8px;
}

.sub-price-unit {
  font-size: 12px;
  font-weight: normal;
  color: #9296A0;
}
</style>
